/**
 * "What lifts the band" — the EXACT pillar-lift rows (`projectPillarShift`
 * only), rendered by the Decision tab's Decide section (`DecideSurface`). The
 * Path to GO Decide phase that also rendered it was deleted in stage 3.
 *
 * Markup moved from `pathToGo/DecisionStage.tsx` (Path to GO removal, stage 1).
 * Test ids are unchanged. Hand-rolled case/letter-spacing utility pairs were
 * dropped because `type-meta` already sets them (uppercase, 0.1em), so the
 * rendered text is identical.
 */
import React from 'react';
import type { LiftRow } from './decisionLift';

export interface DecisionLiftListProps {
  liftRows: readonly LiftRow[];
  /** A withheld verdict has no band to lift; say so instead of implying one. */
  recommendationWithheld: boolean;
  /**
   * Replaces the non-withheld empty-state sentence. The default points at "the
   * open evidence items above", which was right in the deleted Path to GO
   * Decide phase (its "Close N items" CTA sat above) but not on a surface that
   * lists nothing above.
   */
  noLiftMessage?: string;
}

export const DecisionLiftList: React.FC<DecisionLiftListProps> = ({
  liftRows,
  recommendationWithheld,
  noLiftMessage,
}) => (
  <div>
    <div className="type-meta text-text-muted font-bold mb-1">
      What lifts the band
    </div>
    {liftRows.length === 0 ? (
      <div
        className="rounded-lg border border-border bg-background-elevated p-3 type-body text-text-secondary"
        data-testid="decision-no-lifts"
      >
        {/* REG-SCORE #3 — a withheld verdict has no band to lift; say
            so instead of implying a (raw, unearned) band exists. */}
        {recommendationWithheld
          ? 'Verdict withheld until the open decision or evidence gap is closed — no decision band to lift yet.'
          : noLiftMessage
            ?? 'No single pillar fix changes the band — progress comes from closing the open evidence items above.'}
      </div>
    ) : (
      <ul
        className="rounded-lg border border-border bg-background-elevated px-3"
        data-testid="decision-lift-list"
      >
        {liftRows.map((row) => (
          <li
            key={row.pillarId}
            className="flex items-center justify-between gap-2 py-2.5 border-b border-border-subtle last:border-b-0"
            data-testid={`decision-lift-${row.pillarId}`}
            data-to-band={row.toBand}
          >
            <span className="text-[13px] font-bold text-text-primary">
              Strengthen {row.pillarLabel}
            </span>
            <span
              className="shrink-0 type-meta px-1.5 py-0.5 rounded-md border border-success/40 text-success"
              data-testid={`decision-lift-band-${row.pillarId}`}
            >
              → {row.toBandLabel}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default DecisionLiftList;
